import { useState } from 'react';
import { Tag } from 'lucide-react';
import { useCouponValidation } from '../../coupons/hooks/use-coupon-validation.js';
import { formatPaise } from '../../../../lib/format.js';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';

const REJECTION_COPY: Record<string, string> = {
  invalid_code: "That code doesn't exist.",
  inactive: 'This coupon is not currently active.',
  not_yet_active: "This coupon isn't active yet.",
  expired: 'This coupon has expired.',
  usage_limit_reached: "This coupon's usage limit has been reached.",
  user_limit_reached: "You've already used this coupon the maximum number of times.",
  min_order_not_met: "Your cart doesn't meet this coupon's minimum order value.",
  no_eligible_items: "None of the items in your cart are eligible for this coupon.",
};

export interface CouponInputProps {
  /** Called once a code validates successfully -- the page stores it in local state and re-applies it for real at checkout; this component never applies anything itself. */
  onValidCode: (code: string) => void;
}

/**
 * Eligibility/discount **preview** only, via `POST /coupons/validate` --
 * the cart endpoint has no coupon-application state, so nothing here
 * changes `subtotalPaise` or any other cart total. Real application
 * happens at checkout.
 */
export function CouponInput({ onValidCode }: CouponInputProps) {
  const [code, setCode] = useState('');
  const validation = useCouponValidation();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    validation.mutate(trimmed, {
      onSuccess: (result) => {
        if (result.valid) onValidCode(trimmed);
      },
    });
  };

  const result = validation.data;
  const errorText =
    validation.isError && validation.error instanceof StorefrontApiError
      ? validation.error.message
      : validation.isError
        ? 'Could not validate this code. Please try again.'
        : null;

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-100 bg-white p-4">
      <label htmlFor="coupon-code" className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <Tag className="h-4 w-4" aria-hidden="true" />
        Have a coupon?
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="coupon-code"
          type="text"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Enter code"
          className="focus-visible:ring-brand min-w-0 flex-1 rounded-full border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        <button
          type="submit"
          disabled={validation.isPending || !code.trim()}
          className="focus-visible:ring-brand shrink-0 rounded-full bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {validation.isPending ? 'Checking…' : 'Apply'}
        </button>
      </div>

      <div role="status" className="mt-2 text-xs">
        {errorText && <p className="font-medium text-red-600">{errorText}</p>}
        {result && !result.valid && result.reason && (
          <p className="font-medium text-red-600">{REJECTION_COPY[result.reason] ?? 'This coupon cannot be applied.'}</p>
        )}
        {result && result.valid && (
          <p className="font-medium text-green-700">
            Code eligible for {formatPaise(result.discountPaise)} off — it will be applied at checkout.
          </p>
        )}
      </div>
    </form>
  );
}
